/**
 * IndexedDB Service für Offline-Persistenz
 *
 * Stores:
 * - offlineQueue: Ausstehende Operationen für Backend-Sync
 * - shoppingLists: Cache der Shopping Lists
 * - recipes: Cache der Rezepte
 * - bookmarks: Cache der Bookmarks
 * - syncMetadata: Letzte Sync-Zeitstempel und Konfiguration
 */

const DB_NAME = 'myboki-offline';
const DB_VERSION = 1;

export const STORES = {
  QUEUE: 'offlineQueue',
  SHOPPING_LISTS: 'shoppingLists',
  RECIPES: 'recipes',
  BOOKMARKS: 'bookmarks',
  SYNC_META: 'syncMetadata'
};

class OfflineDatabase {
  constructor() {
    this.db = null;
    this.isInitialized = false;
    this.initPromise = null;
  }

  /**
   * Initialisiert die IndexedDB Datenbank
   * @returns {Promise<IDBDatabase>}
   */
  async init() {
    // Singleton - nur einmal initialisieren
    if (this.isInitialized) return this.db;

    // Falls bereits eine Initialisierung läuft, darauf warten
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      // Prüfe ob IndexedDB verfügbar ist
      if (!window.indexedDB) {
        console.warn('[OfflineDB] IndexedDB nicht verfügbar - Offline-Funktionen deaktiviert');
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('[OfflineDB] Fehler beim Öffnen der Datenbank:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        console.log('[OfflineDB] Datenbank erfolgreich geöffnet');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        console.log('[OfflineDB] Upgrade von Version', event.oldVersion, 'zu', event.newVersion);

        // Offline Queue Store
        if (!db.objectStoreNames.contains(STORES.QUEUE)) {
          const queueStore = db.createObjectStore(STORES.QUEUE, {
            keyPath: 'id',
            autoIncrement: true
          });
          queueStore.createIndex('timestamp', 'timestamp', { unique: false });
          queueStore.createIndex('entityType', 'entityType', { unique: false });
          queueStore.createIndex('status', 'status', { unique: false });
          queueStore.createIndex('operation', 'operation', { unique: false });
        }

        // Shopping Lists Cache
        if (!db.objectStoreNames.contains(STORES.SHOPPING_LISTS)) {
          const listsStore = db.createObjectStore(STORES.SHOPPING_LISTS, {
            keyPath: 'id'
          });
          listsStore.createIndex('updatedAt', 'updatedAt', { unique: false });
          listsStore.createIndex('_pendingSync', '_pendingSync', { unique: false });
        }

        // Recipes Cache
        if (!db.objectStoreNames.contains(STORES.RECIPES)) {
          const recipesStore = db.createObjectStore(STORES.RECIPES, {
            keyPath: 'id'
          });
          recipesStore.createIndex('bookmarkId', 'bookmarkId', { unique: false });
          recipesStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Bookmarks Cache
        if (!db.objectStoreNames.contains(STORES.BOOKMARKS)) {
          const bookmarksStore = db.createObjectStore(STORES.BOOKMARKS, {
            keyPath: 'id'
          });
          bookmarksStore.createIndex('categoryId', 'categoryId', { unique: false });
          bookmarksStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        }

        // Sync Metadata
        if (!db.objectStoreNames.contains(STORES.SYNC_META)) {
          db.createObjectStore(STORES.SYNC_META, { keyPath: 'key' });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Stellt sicher, dass die DB initialisiert ist
   */
  async ensureInit() {
    if (!this.isInitialized) {
      await this.init();
    }
  }

  /**
   * Fügt einen Eintrag hinzu (autoIncrement ID)
   * @param {string} storeName
   * @param {object} data
   * @returns {Promise<number>} Die generierte ID
   */
  async add(storeName, data) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.add(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Holt einen Eintrag nach ID
   * @param {string} storeName
   * @param {string|number} id
   * @returns {Promise<object|undefined>}
   */
  async get(storeName, id) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Holt alle Einträge aus einem Store
   * @param {string} storeName
   * @returns {Promise<array>}
   */
  async getAll(storeName) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Holt Einträge nach Index-Wert
   * @param {string} storeName
   * @param {string} indexName
   * @param {any} value
   * @returns {Promise<array>}
   */
  async getByIndex(storeName, indexName, value) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const index = store.index(indexName);
      const request = index.getAll(value);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Aktualisiert oder fügt einen Eintrag hinzu (upsert)
   * @param {string} storeName
   * @param {object} data - Muss keyPath enthalten (id)
   * @returns {Promise<void>}
   */
  async put(storeName, data) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Löscht einen Eintrag nach ID
   * @param {string} storeName
   * @param {string|number} id
   * @returns {Promise<void>}
   */
  async delete(storeName, id) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.delete(id);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Löscht alle Einträge aus einem Store
   * @param {string} storeName
   * @returns {Promise<void>}
   */
  async clear(storeName) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Zählt Einträge in einem Store
   * @param {string} storeName
   * @returns {Promise<number>}
   */
  async count(storeName) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.count();

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Führt mehrere Operationen in einer Transaktion aus
   * @param {string} storeName
   * @param {function} callback - Erhält den ObjectStore
   * @returns {Promise<void>}
   */
  async transaction(storeName, callback) {
    await this.ensureInit();

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);

      callback(store);
    });
  }

  /**
   * Speichert Sync-Metadaten
   * @param {string} key
   * @param {any} value
   */
  async setSyncMeta(key, value) {
    await this.put(STORES.SYNC_META, { key, value, updatedAt: Date.now() });
  }

  /**
   * Holt Sync-Metadaten
   * @param {string} key
   * @returns {Promise<any>}
   */
  async getSyncMeta(key) {
    const result = await this.get(STORES.SYNC_META, key);
    return result?.value;
  }

  /**
   * Löscht die gesamte Datenbank (für Logout/Reset)
   * @returns {Promise<void>}
   */
  async deleteDatabase() {
    return new Promise((resolve, reject) => {
      // Schließe bestehende Verbindung
      if (this.db) {
        this.db.close();
        this.db = null;
        this.isInitialized = false;
        this.initPromise = null;
      }

      const request = indexedDB.deleteDatabase(DB_NAME);
      request.onsuccess = () => {
        console.log('[OfflineDB] Datenbank gelöscht');
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton-Instanz exportieren
export const offlineDb = new OfflineDatabase();
export default offlineDb;
